import { useEffect, useRef, useState } from 'react';
import Dragster from 'dragster';
import uniq from 'lodash/uniq';

import { FileViewModel, Nullable } from 'chaika-frontend-types/frontend-cms';

import { FileApi } from '@api/file-api';
import { store } from '@store';
import { AcceptTypes, FileTypesDictionary } from '@components/FileInput/FileInput';
import { isDefined } from '@utils/isDefined';
import { sanitizeFileName } from '@utils/sanitizeFileName';
import { translate } from '@chaika/i18n/helper';

export interface FileDragAndDropDefaultProps {
    multipleFileLoad?: boolean;
    acceptFileTypes?: AcceptTypes[];
    createFileAssetApi(fileName: string): Promise<FileViewModel> | FileViewModel;
    setFileUploadedStatusApi?(fileName: string): Promise<void>;
    onFileLoaded(asset: FileViewModel): void;
}

export interface FileDragAndDropCustomProps {
    multipleFileLoad?: boolean;
    acceptFileTypes?: AcceptTypes[];
    customFileApi(file: File): Promise<void>;
    onFileLoaded(): void;
    onFileLoadError(message: string): void;
}

export type FileDragAndDropProps = FileDragAndDropDefaultProps | FileDragAndDropCustomProps;

interface Data {
    areaRef: React.RefObject<HTMLDivElement | null>;
    isActive: boolean;
    loadProgress: Nullable<number>;
    onFileAdd(files: File[]): Promise<void>;
}

export function useFileDragAndDrop(props: FileDragAndDropProps): Data {
    const [isActive, setIsActive] = useState(false);
    const [loadProgress, setLoadProgress] = useState<Nullable<number>>(null);
    const areaRef = useRef<HTMLDivElement | null>(null);

    const loadProgressRef = useRef(loadProgress);
    loadProgressRef.current = loadProgress;

    const loadErrorHandler = (message: string): void => {
        store.app.showingPopupParams = {
            title: translate('errorOnFileDownloading'),
            description: message,
        };

        if (hasCustomApi(props)) {
            props.onFileLoadError(message);
        }
    };

    const defaultLoadFile = async (file: File): Promise<void> => {
        const { createFileAssetApi, setFileUploadedStatusApi, onFileLoaded } = props as FileDragAndDropDefaultProps;

        const fileAsset = await createFileAssetApi(file.name);

        for await (const { progress } of FileApi.uploadProcess(file, { fileName: fileAsset.path })) {
            setLoadProgress(progress);
        }

        await setFileUploadedStatusApi?.(file.name);
        onFileLoaded(fileAsset);
    };

    const customLoadFile = async (file: File): Promise<void> => {
        const { customFileApi, onFileLoaded } = props as FileDragAndDropCustomProps;
        setLoadProgress(100);

        await customFileApi(file);

        onFileLoaded();
    };

    const loadFile = async (file: File): Promise<void> => {
        if (hasCustomApi(props)) {
            await customLoadFile(file);
        } else {
            await defaultLoadFile(file);
        }
    };

    const onFileAdd = async (files: File[]): Promise<void> => {
        setLoadProgress(0);
        setIsActive(false);

        try {
            checkFiles(files, props.acceptFileTypes, props.multipleFileLoad);

            for (const file of files) {
                await loadFile(new File([file], sanitizeFileName(file.name), { type: file.type }));
            }
        } catch (error: any) {
            loadErrorHandler(error.message);
        }

        setLoadProgress(null);
    };

    const onFileAddRef = useRef(onFileAdd);
    onFileAddRef.current = onFileAdd;

    useEffect(() => {
        const element = areaRef.current;
        if (!element) {
            return;
        }

        const dragster = new Dragster(element);

        const handleDragEnter = () => setIsActive(true);
        const handleDragLeave = () => setIsActive(false);
        const handleDragOver = (event: DragEvent) => event.preventDefault();
        const handleDrop = (event: DragEvent) => {
            const files = event.dataTransfer?.files;
            event.stopPropagation();
            event.preventDefault();
            if (files?.length && loadProgressRef.current === null) {
                onFileAddRef.current(Array.from(files));
            }
        };

        element.addEventListener('dragster:enter', handleDragEnter);
        element.addEventListener('dragster:leave', handleDragLeave);
        element.addEventListener('drop', handleDrop);
        element.addEventListener('dragover', handleDragOver);

        return () => {
            dragster.removeListeners();
            element.removeEventListener('dragster:enter', handleDragEnter);
            element.removeEventListener('dragster:leave', handleDragLeave);
            element.removeEventListener('drop', handleDrop);
            element.removeEventListener('dragover', handleDragOver);
        };
    }, []);

    return { areaRef, isActive, loadProgress, onFileAdd };
}

function hasCustomApi(props: FileDragAndDropProps): props is FileDragAndDropCustomProps {
    return !!(props as FileDragAndDropCustomProps).customFileApi;
}

function checkFiles(files: File[], acceptFileTypes?: AcceptTypes[], multipleFileLoad?: boolean): void {
    const isAcceptFilesTypesChecked = checkAcceptFilesTypes(files, acceptFileTypes);
    const isFilesCountCorrect = isDefined(multipleFileLoad) && !multipleFileLoad ? files.length === 1 : true;

    if (!isAcceptFilesTypesChecked || !isFilesCountCorrect) {
        const errorMessages: string[] = [];
        if (!isAcceptFilesTypesChecked) {
            errorMessages.push(
                `${translate('incorrectFileType')}: ${uniq(acceptFileTypes)
                    .map(type => FileTypesDictionary[type])
                    .join(', ')}`,
            );
        }
        if (!isFilesCountCorrect) {
            errorMessages.push(translate('invalidFileNumbers'));
        }

        throw new Error(errorMessages.join('. \n'));
    }
}

function checkAcceptFilesTypes(files: File[], acceptFileTypes?: AcceptTypes[]): boolean {
    return !!acceptFileTypes?.length
        ? //@ts-expect-error
          !files.some(file => !acceptFileTypes.includes(file.type))
        : true;
}
